import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useState } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9otrhaqs38sj6jc7fqg';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于动漫之家 - 韩国漫画无删减免费阅读平台',
  description: '动漫之家是国内领先的韩国漫画在线阅读平台，专注提供无删减、高清、下拉式韩漫阅读体验。了解我们的品牌故事、用户口碑和联系方式。',
  url: 'https://dongmanzhijia.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '动漫之家',
    url: 'https://dongmanzhijia.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '动漫之家上的韩国漫画是否全部免费？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，动漫之家平台上的所有韩国漫画均为免费阅读。我们致力于降低优质漫画的获取门槛，让更多读者能够享受高清无删减的韩漫阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '动漫之家的漫画更新频率是怎样的？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '动漫之家每日同步更新热门韩漫作品，确保读者能够在第一时间阅读到最新章节。所有更新均为无删减版本，与韩国原版保持同步。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在动漫之家搜索想看的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过网站顶部的搜索功能输入漫画名称或作者名进行搜索，也可以通过分类浏览页面按类型（恋爱、奇幻、动作等）筛选感兴趣的漫画作品。',
      },
    },
    {
      '@type': 'Question',
      name: '动漫之家的下拉式阅读是什么意思？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '下拉式阅读是一种优化的漫画阅读方式，读者只需向下滑动页面即可连续阅读，无需翻页或点击。这种方式特别适合手机和电脑端的沉浸式阅读体验，加载流畅、操作简便。',
      },
    },
    {
      '@type': 'Question',
      name: '为什么选择动漫之家阅读韩国漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '动漫之家拥有以下优势：1) 全部无删减正版内容；2) 高清画质保证最佳阅读体验；3) 下拉式流畅阅读；4) 实时更新与韩国同步；5) 丰富分类满足不同口味；6) 完全免费无需付费。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string>('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setShowSuccessToast(true);
      form.reset();
      setTimeout(() => setShowSuccessToast(false), 3000);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        serverMsg = parsed?.meta?.message || parsed?.meta?.detail || '';
      } catch {
        serverMsg = responseText;
      }

      if (response.ok && !serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('success');
        setShowSuccessToast(true);
        form.reset();
        setTimeout(() => {
          setShowSuccessToast(false);
          setFormStatus('idle');
        }, 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍·口碑·联系方式"
        description="动漫之家是国内领先的韩国漫画在线阅读平台，专注提供无删减、高清、下拉式韩漫阅读体验。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="动漫之家,关于我们,韩国漫画平台,韩漫在线阅读,品牌介绍,联系方式"
        canonicalUrl="https://dongmanzhijia.com/about"
        ogTitle="关于动漫之家 - 韩国漫画无删减免费阅读平台"
        ogDescription="了解动漫之家的品牌故事、用户口碑和联系方式。国内领先的韩国漫画在线阅读平台。"
        schemaData={[aboutSchema, faqSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          <section id="brand" className="py-16 md:py-20 bg-background-50" aria-labelledby="brand-heading">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h1 id="brand-heading" className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                  品牌介绍
                </h1>
                <p className="text-sm text-foreground-500 mt-3 max-w-2xl mx-auto">
                  动漫之家 —— 专注于韩国漫画的优质在线阅读平台
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-800 mb-4">
                    我们的故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    动漫之家成立于2022年，由一群热爱韩国漫画的年轻人共同创立。创始团队在留学韩国期间，深深被韩国漫画独特的画风、细腻的叙事和丰富的题材所吸引。回国后，他们发现国内缺乏一个能够完整、高清呈现韩国漫画的优质平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    于是，动漫之家应运而生。我们坚持"无删减、高画质、全免费"的核心理念，采用创新的<strong>下拉式阅读</strong>模式，让读者能够沉浸在流畅的漫画世界中。从最初的数十部作品起步，到如今拥有超过<strong>2000部</strong>精选韩国漫画，动漫之家已经成长为国内领先的韩漫阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们相信，好的故事不应该被删减，好的作品应该被更多人看到。动漫之家将继续努力，为读者带来更多优质的韩国漫画作品。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20with%20Korean%20webtoon%20art%20displayed%20on%20large%20screens%2C%20young%20Asian%20team%20working%20on%20comics%2C%20warm%20ambient%20lighting%2C%20cozy%20workspace%20with%20plants%20and%20art%20supplies%2C%20clean%20minimalist%20design%2C%20inspirational%20atmosphere%2C%20soft%20coral%20and%20warm%20neutral%20tones&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="动漫之家品牌故事 - 韩国漫画创作团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14">
                {[
                  { value: '2022', label: '成立年份' },
                  { value: '2000+', label: '精品漫画' },
                  { value: '500万+', label: '累计读者' },
                  { value: '99.9%', label: '好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100">
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="reputation" className="py-16 md:py-20 bg-background-100" aria-labelledby="reputation-heading">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 id="reputation-heading" className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mt-3">
                  来自500万读者的真实评价
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    name: '漫画迷小王',
                    rating: 5,
                    text: '动漫之家真的是我见过最好的韩漫平台！画质清晰，更新速度快，而且完全没有删减内容。下拉式阅读体验太棒了，一口气能看几十话，强烈推荐！',
                    date: '2026年7月',
                  },
                  {
                    name: '月亮不睡',
                    rating: 5,
                    text: '从高中开始看韩漫，换了很多平台，最后留在了动漫之家。分类很全面，恋爱、奇幻、动作啥都有，而且真的全部免费。良心平台，希望越做越好！',
                    date: '2026年7月',
                  },
                  {
                    name: '追漫达人小李',
                    rating: 5,
                    text: '无删减这点太重要了！之前在其他平台看的漫画总觉得少了点什么，来了动漫之家才发现原来很多剧情被切掉了。画质也是真的高清，手机上看体验也很好。',
                    date: '2026年8月',
                  },
                  {
                    name: '橘子汽水',
                    rating: 5,
                    text: '朋友推荐过来的，一看就停不下来。下拉式阅读太舒服了，不用一直点翻页，躺在床上就能轻松追漫。更新和韩国那边基本同步，追更党狂喜！',
                    date: '2026年8月',
                  },
                  {
                    name: '漫画研究员',
                    rating: 5,
                    text: '作为漫画爱好者，我特别看重作品的完整度。动漫之家的无删减承诺让我很放心，而且分类做得很好，找漫画很方便。希望能引进更多优秀的韩国漫画作品。',
                    date: '2026年7月',
                  },
                  {
                    name: '冰美式不加糖',
                    rating: 5,
                    text: '用了大半年了，整体体验非常好。高清画质真的不一样，细节看得清清楚楚。热门漫画更新也很快，每天都有新内容可以看。唯一的建议就是能不能加个夜间模式。',
                    date: '2026年8月',
                  },
                ].map((review, index) => (
                  <div key={index} className="bg-background-50 rounded-xl p-6 flex flex-col">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-4">
                      "{review.text}"
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground-800">{review.name}</span>
                      <span className="text-xs text-foreground-400">{review.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-16 md:py-20 bg-background-50" aria-labelledby="contact-heading">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 id="contact-heading" className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mt-3">
                  有任何问题或建议？欢迎随时联系我们
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-800 mb-6">
                    联系方式
                  </h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@dongmanzhijia.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">微信公众号</p>
                        <p className="text-sm text-foreground-500">动漫之家DMZJ</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-weibo-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">官方微博</p>
                        <p className="text-sm text-foreground-500">@动漫之家官方</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">公司地址</p>
                        <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO T2-B座 1208</p>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-heading font-bold text-foreground-800 mt-8 mb-4">
                    商务合作
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们欢迎各类商务合作，包括但不限于：漫画版权引进、广告投放、联合推广、IP授权等。如有合作意向，请通过以上联系方式与我们取得联系，我们将在24小时内回复。
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-800 mb-6">
                    在线留言
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form>
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                      </div>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送留言
                      </button>
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formError}</p>
                      )}
                    </form>
                </div>
              </div>
            </div>
          </section>

          {showSuccessToast && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-900 text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium animate-fade-in-up">
              <i className="ri-check-line mr-2"></i> 留言已成功提交，我们会尽快回复您！
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}